export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'theme-mode';

type Snapshot = {
  mode: ThemeMode;
  resolvedMode: ResolvedThemeMode;
};

type Listener = () => void;

let isInitialized = false;
let snapshot: Snapshot = { mode: 'dark', resolvedMode: 'dark' };
const listeners = new Set<Listener>();

let mediaQuery: MediaQueryList | null = null;
let mediaQueryListener: ((e: MediaQueryListEvent) => void) | null = null;

function getSystemTheme(): ResolvedThemeMode {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function resolveTheme(mode: ThemeMode): ResolvedThemeMode {
  return mode === 'system' ? getSystemTheme() : mode;
}

function applyThemeToDocument(resolvedMode: ResolvedThemeMode): void {
  document.documentElement.setAttribute('data-theme', resolvedMode);
}

function notify(): void {
  listeners.forEach((l) => l());
}

function readStoredMode(): ThemeMode | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return null;
}

function attachSystemListenerIfNeeded(mode: ThemeMode): void {
  if (typeof window === 'undefined') return;

  if (mode !== 'system') {
    if (mediaQuery && mediaQueryListener) {
      mediaQuery.removeEventListener('change', mediaQueryListener);
    }
    mediaQuery = null;
    mediaQueryListener = null;
    return;
  }

  if (!mediaQuery) {
    mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
  }

  if (!mediaQueryListener) {
    mediaQueryListener = (e: MediaQueryListEvent) => {
      const newResolved: ResolvedThemeMode = e.matches ? 'light' : 'dark';
      snapshot = { ...snapshot, resolvedMode: newResolved };
      applyThemeToDocument(newResolved);
      notify();
    };
    mediaQuery.addEventListener('change', mediaQueryListener);
  }
}

export function initThemeStore(defaultMode: ThemeMode = 'dark'): void {
  if (typeof window === 'undefined') return;
  if (isInitialized) return;
  isInitialized = true;

  const storedMode = readStoredMode() ?? defaultMode;
  const resolved = resolveTheme(storedMode);

  snapshot = { mode: storedMode, resolvedMode: resolved };
  applyThemeToDocument(resolved);
  attachSystemListenerIfNeeded(storedMode);
}

export function getThemeSnapshot(): Snapshot {
  return snapshot;
}

export function subscribeTheme(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setThemeMode(mode: ThemeMode): void {
  if (typeof window === 'undefined') return;
  if (!isInitialized) initThemeStore('dark');

  const resolved = resolveTheme(mode);
  snapshot = { mode, resolvedMode: resolved };
  localStorage.setItem(THEME_STORAGE_KEY, mode);
  applyThemeToDocument(resolved);
  attachSystemListenerIfNeeded(mode);
  notify();
}

export function toggleThemeMode(): void {
  const current = snapshot.resolvedMode;
  setThemeMode(current === 'dark' ? 'light' : 'dark');
}
